/**
 * FormRenderer - Standalone form rendering component
 * Renders form content and captures user input
 * Completely decoupled from modals, pages, or any container
 * 
 * Usage:
 * <FormRenderer
 *   content={htmlContent}
 *   onFormDataChange={(data) => console.log(data)}
 *   showSignature={true}
 *   readOnly={false}
 * />
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import SignaturePad from '../SignaturePad';
import './styles/form-display.css';

const FormRendererComponent = ({
  content,
  onFormDataChange,
  onValidationChange,
  showSignature = false,
  acceptanceMode = 'signature',
  documentTitle = '',
  readOnly = false,
  initialFormData = {},
  className = ''
}, ref) => {
  const [formData, setFormData] = useState(initialFormData);
  const [signatureData, setSignatureData] = useState(initialFormData.signature || null);
  const [signaturePlaceholders, setSignaturePlaceholders] = useState([]);
  const contentRef = useRef(null);

  // Mount SignaturePad components in signature pad containers
  useEffect(() => {
    if (contentRef.current && showSignature && acceptanceMode === 'accept') {
      const signaturePads = contentRef.current.querySelectorAll('.form-signature-pad');
      const signaturePadElements = Array.from(signaturePads).map(pad => {
        pad.innerHTML = '';
        return {
          element: pad,
          instructionText: pad.getAttribute('data-instruction-text') ||
            'By providing your digital signature below you agree to the terms and conditions of this document',
          legalNoticeText: pad.getAttribute('data-legal-notice-text') ||
            'Your digital signature will be timestamped and legally binding. This document will be securely stored and available for download.'
        };
      });
      setSignaturePlaceholders(signaturePadElements);
    }
  }, [content, showSignature, acceptanceMode]);

  // Capture form data from DOM whenever inputs change
  useEffect(() => {
    if (!contentRef.current || readOnly) return;

    const handleInputChange = () => {
      const capturedData = captureFormData();
      setFormData(capturedData);
    };

    const contentElement = contentRef.current;

    // Use single 'input' event listener (covers most cases)
    contentElement.addEventListener('input', handleInputChange);
    // Use 'change' for select/radio/checkbox to ensure we catch those
    contentElement.addEventListener('change', handleInputChange);

    return () => {
      contentElement.removeEventListener('input', handleInputChange);
      contentElement.removeEventListener('change', handleInputChange);
    };
  }, [content, readOnly]);

  const captureFormData = () => {
    if (!contentRef.current) return {};

    const captured = {};
    const element = contentRef.current;

    // Capture text inputs
    const inputs = element.querySelectorAll('input.form-text, input.form-email, input.form-number, input.form-tel');
    inputs.forEach(input => {
      if (input.name) {
        captured[input.name] = input.value;
      }
    });

    // Capture textareas
    const textareas = element.querySelectorAll('textarea[class^="form-"]');
    textareas.forEach(textarea => {
      if (textarea.name) {
        captured[textarea.name] = textarea.value;
      }
    });

    // Capture select dropdowns
    const selects = element.querySelectorAll('select[class^="form-"]');
    selects.forEach(select => {
      if (select.name) {
        captured[select.name] = select.value;
      }
    });

    // Capture checkboxes
    const checkboxes = element.querySelectorAll('input[type="checkbox"][class^="form-"]');
    checkboxes.forEach(checkbox => {
      if (checkbox.name) {
        const label = checkbox.closest('label');
        const textSpan = label?.querySelector('.form-checkbox-text');
        const checkboxText = textSpan?.textContent || '';
        
        const uniqueFieldName = checkboxText
          ? checkboxText.toLowerCase().replace(/[^a-z0-9]+/g, '_')
          : checkbox.name;

        captured[uniqueFieldName] = checkbox.checked;
      }
    });

    // Capture radio buttons
    const radios = element.querySelectorAll('input[type="radio"][class^="form-"]:checked');
    radios.forEach(radio => {
      if (radio.name) {
        captured[radio.name] = radio.value;
      }
    });

    return captured;
  };

  const handleSignatureChange = (signature) => {
    setSignatureData(signature);
  };

  // Trigger validation and notify parent whenever form data or signature changes
  useEffect(() => {
    const combinedData = { ...formData, signature: signatureData };

    // Notify parent of data changes
    if (onFormDataChange) {
      onFormDataChange(combinedData);
    }

    // Notify parent of validation changes
    if (onValidationChange) {
      const valid = isFormValid();
      onValidationChange(valid);
    }
  }, [formData, signatureData]);

  // Check if form is valid (all required fields filled + signature if needed)
  const isFormValid = () => {
    if (!contentRef.current) return false;

    // Check signature if signature pads exist
    if (signaturePlaceholders.length > 0 && !signatureData) {
      return false;
    }

    // Check if all required fields are filled
    const requiredFields = contentRef.current.querySelectorAll('[required]');

    // Track which radio groups we've already validated
    const validatedRadioGroups = new Set();

    for (const field of requiredFields) {
      if (field.type === 'radio') {
        // For radio buttons, check if any in the group is checked
        if (!validatedRadioGroups.has(field.name)) {
          const radioGroup = contentRef.current.querySelectorAll(`input[type="radio"][name="${field.name}"]`);
          const isAnyChecked = Array.from(radioGroup).some(radio => radio.checked);
          if (!isAnyChecked) {
            return false;
          }
          validatedRadioGroups.add(field.name);
        }
      } else if (field.type === 'checkbox') {
        // For required checkboxes, must be checked
        if (!field.checked) {
          return false;
        }
      } else {
        // For text inputs, select, textarea - require non-empty value
        const value = field.value?.trim();
        if (!value) {
          return false;
        }
      }
    }

    return true;
  };

  // Expose methods via ref
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref({
          getFormData: () => {
            return { ...formData, signature: signatureData };
          },
          isFormValid: isFormValid,
          getSignature: () => signatureData
        });
      } else if (ref.current) {
        ref.current.getFormData = () => {
          return { ...formData, signature: signatureData };
        };
        ref.current.isFormValid = isFormValid;
        ref.current.getSignature = () => signatureData;
      }
    }
  }, [formData, signatureData, ref]);

  return (
    <>
      <div
        ref={contentRef}
        className={`form-renderer ${className}`}
        dangerouslySetInnerHTML={{ __html: content || '<p>No content available</p>' }}
      />

      {/* Render SignaturePad components in their placeholder locations */}
      {signaturePlaceholders.map((placeholder, index) =>
        createPortal(
          <SignaturePad
            key={`signature-${index}`}
            onSignatureChange={handleSignatureChange}
            required={true}
            disabled={readOnly}
            instructionText={placeholder.instructionText}
            showLegalNotice={true}
            legalNoticeText={placeholder.legalNoticeText}
            documentTitle={documentTitle}
          />,
          placeholder.element
        )
      )}
    </>
  );
};

const FormRenderer = React.forwardRef(FormRendererComponent);

export default FormRenderer;
export { FormRenderer };
